// Stings and knocks are made here with WebAudio; the night beds and three special sounds are the owner's CC0 files in
// client/audio/ (see studio/AUDIO-LOG.md), fetched only when first needed. Nothing plays until the first gesture, a muted
// client builds and fetches nothing, and the Halloween edition swaps in spooky variants of the knock and stings.
export const MUTE_KEY = 'door.mute';
export const EVENTS = ['knock', 'hum', 'right', 'wrong', 'alien', 'infected', 'siren', 'tick', 'thunk', 'sting-right', 'sting-wrong-open', 'sting-wrong-shut', 'paper', 'seal', 'mail-big', 'mail-gilded'];
const SPOOKY = new Set(['knock', 'right', 'wrong', 'alien', 'infected']);

export const soundFor = (event, halloween) => (halloween && SPOOKY.has(event) ? `${event}-halloween` : event);
export const muteLabel = muted => (muted ? 'Sound: off' : 'Sound: on');

// What the verdict sounds like: the doorkeeper hears the reveal kind on a wrong call, everyone hears right or wrong.
export function revealEvent(role, m) {
  if (m.points > 0) return 'right';
  if (role === 'doorkeeper' && m.verdict === 'open' && (m.kind === 'alien' || m.kind === 'infected')) return m.kind;
  return 'wrong';
}

// The files, by name. FILE_GAIN is each one's level under the master gain (measured by studio/tools/audio-check.js).
const FILE_URL = { 'rain-loop': '/audio/rain-loop.mp3', thunder: '/audio/thunder.mp3', 'outbreak-loop': '/audio/outbreak-loop.mp3', 'outbreak-sirens': '/audio/outbreak-sirens.mp3', 'alien-reveal': '/audio/alien-reveal.mp3', 'bull-knock': '/audio/bull-knock.mp3', 'bear-knock': '/audio/bear-knock.mp3' };
export const FILE_GAIN = { 'rain-loop': 0.3, thunder: 0.18, 'outbreak-loop': 0.18, 'outbreak-sirens': 0.2, 'alien-reveal': 0.35, 'bull-knock': 0.5, 'bear-knock': 0.35 };
const SPECIALS = new Set(['alien-reveal', 'bull-knock', 'bear-knock']);
export const RAIN_DUCK = 0.35, FADE_S = 4, SILENT = 0.0001;
export const THUNDER_MIN_GAP_MS = 20000, THUNDER_SPREAD_MS = 40000;
const BUSY_MS = { knock: 1200, right: 2500, wrong: 2500, alien: 2500, infected: 2500, siren: 2800, thunk: 1500, 'sting-right': 2500, 'sting-wrong-open': 2500, 'sting-wrong-shut': 2500, paper: 600, seal: 400, 'mail-big': 2000, 'mail-gilded': 3000 }; // thunder keeps clear of these

// The call-lands hooks (client/call-lands.js HOOKS): the ka-thunk on the click, one sting at the land (gold and red doors play bigger).
export const STING_EVENTS = { 'sting-right': 'sting-right', 'sting-wrong-open': 'sting-wrong-open', 'sting-wrong-shut': 'sting-wrong-shut' };
export function cueSound(sound, hook, d = {}, halloween = false) {
  if (hook === 'door:thunk') return sound.play('thunk', halloween, d.size);
  if (hook === 'door:land' && STING_EVENTS[d.sting]) return sound.play(STING_EVENTS[d.sting], halloween, d.size);
  return null;
}

export const specialFor = m => (m?.special === 'bull' ? 'bull-knock' : m?.special === 'bear' ? 'bear-knock' : null);
export const revealFileFor = m => (m?.kind === 'alien' ? 'alien-reveal' : null);

const noiseBuffers = new WeakMap();
function noiseBuffer(ctx) {
  if (!noiseBuffers.has(ctx)) {
    const n = Math.floor(ctx.sampleRate * 0.5), b = ctx.createBuffer(1, n, ctx.sampleRate), d = b.getChannelData(0);
    let s = 12345; for (let i = 0; i < n; i++) { s = (s * 1103515245 + 12345) & 0x7fffffff; d[i] = s / 0x3fffffff - 1; }
    noiseBuffers.set(ctx, b);
  }
  return noiseBuffers.get(ctx);
}

// tone: an oscillator gliding f0 to f1 over d seconds with a quick attack and an exponential fade; vib adds a wobble in Hz.
function tone(ctx, out, t, { type = 'sine', f0, f1 = f0, d = 0.2, g = 0.5, vib = 0 }) {
  const o = ctx.createOscillator(), a = ctx.createGain();
  o.type = type;
  o.frequency.setValueAtTime(f0, t); o.frequency.linearRampToValueAtTime(f1, t + d);
  if (vib) { const l = ctx.createOscillator(), lg = ctx.createGain(); l.frequency.setValueAtTime(6, t); lg.gain.setValueAtTime(vib, t); l.connect(lg); lg.connect(o.frequency); l.start(t); l.stop(t + d + 0.05); }
  a.gain.setValueAtTime(0.0001, t); a.gain.linearRampToValueAtTime(g, t + 0.01); a.gain.exponentialRampToValueAtTime(0.0001, t + d);
  o.connect(a); a.connect(out); o.start(t); o.stop(t + d + 0.05);
}
// burst: filtered noise (a click, a cough, a rattle).
function burst(ctx, out, t, { f = 800, q = 1, d = 0.1, g = 0.4, type = 'bandpass' }) {
  const s = ctx.createBufferSource(), fl = ctx.createBiquadFilter(), a = ctx.createGain();
  s.buffer = noiseBuffer(ctx); fl.type = type; fl.frequency.setValueAtTime(f, t); fl.Q.setValueAtTime(q, t);
  a.gain.setValueAtTime(g, t); a.gain.exponentialRampToValueAtTime(0.0001, t + d);
  s.connect(fl); fl.connect(a); a.connect(out); s.start(t); s.stop(t + d + 0.05);
}
const notes = (ctx, out, t, list, opts) => list.forEach(([f, at, d]) => tone(ctx, out, t + at, { f0: f, d, ...opts }));

// Each generator schedules its sound at time t on `out`; a looping one returns a function that stops it.
export const GENERATORS = {
  knock(ctx, out, t) { // a wooden rap: band-passed noise body, a short triangle at the board's pitch, a soft thump under
    burst(ctx, out, t, { f: 600, q: 2, d: 0.07, g: 6 });
    tone(ctx, out, t, { type: 'triangle', f0: 200, f1: 180, d: 0.08, g: 0.5 });
    tone(ctx, out, t, { f0: 110, f1: 55, d: 0.12, g: 0.25 });
  },
  'knock-halloween'(ctx, out, t) { // a hollow rap with a creak of old wood after it
    burst(ctx, out, t, { f: 450, q: 2.5, d: 0.08, g: 9 });
    tone(ctx, out, t, { type: 'triangle', f0: 190, f1: 170, d: 0.1, g: 0.5 });
    tone(ctx, out, t, { f0: 90, f1: 45, d: 0.2, g: 0.15 });
    tone(ctx, out, t + 0.06, { type: 'sawtooth', f0: 140, f1: 220, d: 0.4, g: 0.06, vib: 14 });
    burst(ctx, out, t + 0.06, { f: 700, q: 6, d: 0.35, g: 0.4 });
  },
  hum(ctx, out, t) { // mains hum: 50 Hz and a beating neighbour, plus odd harmonics that small speakers can reproduce
    const a = ctx.createGain(), os = [[50, 0.8], [100.6, 0.8], [150, 0.6], [250, 1.2], [350, 1]].map(([f, g]) => {
      const o = ctx.createOscillator(), h = ctx.createGain(); o.type = 'sine'; o.frequency.setValueAtTime(f, t); h.gain.setValueAtTime(g, t); o.connect(h); h.connect(a); o.start(t); return o;
    });
    a.gain.setValueAtTime(0.0001, t); a.gain.linearRampToValueAtTime(0.02, t + 0.5); a.connect(out);
    return () => { a.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.1); for (const o of os) o.stop(ctx.currentTime + 0.5); };
  },
  right(ctx, out, t) { notes(ctx, out, t, [[659, 0, 0.12], [880, 0.1, 0.25]], { type: 'triangle', g: 1 }); },
  'right-halloween'(ctx, out, t) { notes(ctx, out, t, [[440, 0, 0.18], [523, 0.16, 0.18], [659, 0.32, 0.5]], { type: 'triangle', g: 0.8, vib: 3 }); }, // music box, A minor
  wrong(ctx, out, t) { notes(ctx, out, t, [[220, 0, 0.18], [165, 0.16, 0.4]], { type: 'square', g: 0.5 }); },
  'wrong-halloween'(ctx, out, t) { notes(ctx, out, t, [[110, 0, 0.9], [155.6, 0, 0.9]], { type: 'sawtooth', g: 0.4 }); }, // A and E flat, a tritone
  alien(ctx, out, t) { tone(ctx, out, t, { type: 'sawtooth', f0: 200, f1: 900, d: 0.9, g: 0.6, vib: 40 }); },
  'alien-halloween'(ctx, out, t) { // theremin slide into a minor cluster
    tone(ctx, out, t, { type: 'sine', f0: 300, f1: 880, d: 0.9, g: 0.7, vib: 25 });
    notes(ctx, out, t + 0.6, [[220, 0, 0.9], [262, 0, 0.9], [330, 0, 0.9]], { type: 'sawtooth', g: 0.24 });
  },
  infected(ctx, out, t) { for (let i = 0; i < 3; i++) burst(ctx, out, t + i * 0.22, { f: 700 - i * 100, q: 2, d: 0.15, g: 1 }); tone(ctx, out, t, { f0: 120, f1: 70, d: 0.7, g: 0.4 }); },
  'infected-halloween'(ctx, out, t) { // a rattling cough over a minor drone
    for (let i = 0; i < 4; i++) burst(ctx, out, t + i * 0.16, { f: 500, q: 8, d: 0.12, g: 0.9 });
    notes(ctx, out, t, [[110, 0, 1.2], [131, 0, 1.2]], { type: 'sawtooth', g: 0.24 });
  },
  thunk(ctx, out, t, z = 0) { // the stamp hitting the glass: a dull knock and a thump, over inside a quarter of a second
    burst(ctx, out, t, { f: 400, q: 1.5, d: 0.06, g: 5 });
    tone(ctx, out, t, { f0: 130, f1: 60, d: 0.14, g: 0.6 });
    if (z) tone(ctx, out, t, { f0: 80, f1: 40, d: 0.2, g: 0.5 });
  },
  'sting-right'(ctx, out, t, z = 0) { // a short warm beat: A4 and E5 in soft triangles, a higher glow for each size up
    notes(ctx, out, t, [[440, 0, 0.3], [659, 0.08, 0.4]], { type: 'triangle', g: 0.7 });
    if (z) notes(ctx, out, t, [[880, 0.16, 0.6], [1319, 0.24, 0.7]], { type: 'sine', g: 0.4 });
    if (z > 1) notes(ctx, out, t, [[1760, 0.32, 0.9], [220, 0, 1]], { type: 'sine', g: 0.4 });
  },
  'sting-wrong-open'(ctx, out, t, z = 0) { // the lunge and the flash: a hard low hit with noise; bigger doors add a tritone and a long drop
    burst(ctx, out, t, { f: 300, q: 1, d: 0.15, g: 6 });
    tone(ctx, out, t, { type: 'square', f0: 160, f1: 50, d: 0.4, g: 0.6 });
    if (z) notes(ctx, out, t, [[110, 0.05, 0.8], [155.6, 0.05, 0.8]], { type: 'sawtooth', g: 0.3 });
    if (z > 1) { burst(ctx, out, t + 0.1, { f: 200, q: 1, d: 0.5, g: 4 }); tone(ctx, out, t + 0.1, { type: 'sawtooth', f0: 70, f1: 35, d: 1.1, g: 0.4 }); }
  },
  'sting-wrong-shut'(ctx, out, t, z = 0) { // a person kept out: two soft falling notes, nothing harsh
    notes(ctx, out, t, [[330, 0, 0.25], [247, 0.14, 0.4]], { type: 'sine', g: 0.8 });
    if (z) notes(ctx, out, t, [[165, 0.2, 0.6]], { type: 'triangle', g: 0.6 });
    if (z > 1) notes(ctx, out, t, [[110, 0.3, 0.9], [123, 0.3, 0.9]], { type: 'triangle', g: 0.5 });
  },
  // The envelope (sprint 28 item 3), all made here like the stings: no new files.
  paper(ctx, out, t) { // an envelope sliding on boards: a soft rising swish of filtered noise
    burst(ctx, out, t, { f: 2200, q: 0.8, d: 0.3, g: 0.7, type: 'highpass' });
    burst(ctx, out, t + 0.05, { f: 900, q: 0.6, d: 0.25, g: 0.5 });
  },
  seal(ctx, out, t) { // the wax cracking: a dry click and a small falling tone
    burst(ctx, out, t, { f: 3200, q: 4, d: 0.03, g: 2 });
    tone(ctx, out, t, { type: 'triangle', f0: 900, f1: 500, d: 0.09, g: 0.3 });
  },
  'mail-big'(ctx, out, t) { // sting-right pitched up, over a thunk
    notes(ctx, out, t, [[587, 0, 0.3], [880, 0.08, 0.4]], { type: 'triangle', g: 0.7 });
    notes(ctx, out, t, [[1175, 0.16, 0.6], [1760, 0.24, 0.7]], { type: 'sine', g: 0.4 });
    GENERATORS.thunk(ctx, out, t + 0.02, 1);
  },
  'mail-gilded'(ctx, out, t) { // the big sting with a rising shimmer, two tones a fifth apart
    GENERATORS['sting-right'](ctx, out, t, 2);
    tone(ctx, out, t + 0.2, { type: 'sine', f0: 880, f1: 1760, d: 1.4, g: 0.3 });
    tone(ctx, out, t + 0.2, { type: 'sine', f0: 1320, f1: 2640, d: 1.4, g: 0.25 });
  },
  tick(ctx, out, t) { // one short chalk tap for each step of the sum on the door; the same in every edition
    burst(ctx, out, t, { f: 2600, q: 3, d: 0.035, g: 1.5 });
    tone(ctx, out, t, { type: 'triangle', f0: 1500, f1: 1100, d: 0.04, g: 0.1 });
  },
  siren(ctx, out, t) { for (let i = 0; i < 4; i++) tone(ctx, out, t + i * 0.6, { type: 'triangle', f0: 500, f1: 820, d: 0.58, g: 0.6 }); },
};

// ---- the night around the door ----
// The weather the rain moves between, every minute or two, with a slow crossfade. rain scales the rain's gain, cut is its
// low-pass in Hz (lower sounds further off), thunder is the chance a due roll of thunder goes ahead, w is how often it comes.
export const WEATHER = [
  { id: 'downpour', rain: 1.15, cut: 12000, thunder: 1, w: 2 },
  { id: 'steady', rain: 1, cut: 8000, thunder: 1, w: 4 },
  { id: 'light', rain: 0.6, cut: 4500, thunder: 0.4, w: 3 },
  { id: 'easing', rain: 0.32, cut: 2600, thunder: 0, w: 2 },
];
export const WEATHER_MIN_MS = 45000, WEATHER_SPREAD_MS = 75000, WEATHER_FADE_S = 10;
export const AMBIENT_MIN_GAP_MS = 15000, AMBIENT_SPREAD_MS = 35000;
const pick = (list, r, w = x => x.w) => {
  let k = r * list.reduce((n, x) => n + w(x), 0);
  for (const x of list) { k -= w(x); if (k < 0) return x; }
  return list.at(-1);
};

// noise through a filter that sweeps f0 to fPeak to f1 while the level swells up to g and back down: tyres, wind
function swell(ctx, out, t, { d, peakAt = 0.5, f0, fPeak, f1, q = 1, g, type = 'bandpass' }) {
  const s = ctx.createBufferSource(), fl = ctx.createBiquadFilter(), a = ctx.createGain(), tp = t + d * peakAt;
  s.buffer = noiseBuffer(ctx); s.loop = true; fl.type = type; fl.Q.setValueAtTime(q, t);
  fl.frequency.setValueAtTime(f0, t); fl.frequency.linearRampToValueAtTime(fPeak, tp); fl.frequency.linearRampToValueAtTime(f1, t + d);
  a.gain.setValueAtTime(0.0001, t); a.gain.linearRampToValueAtTime(g, tp); a.gain.linearRampToValueAtTime(0.0001, t + d);
  s.connect(fl); fl.connect(a); a.connect(out); s.start(t); s.stop(t + d + 0.05);
}
// a low-pass in front of out, for a sound heard from further away
function far(ctx, out, f) { const fl = ctx.createBiquadFilter(); fl.type = 'lowpass'; fl.frequency.setValueAtTime(f, 0); fl.connect(out); return fl; }

// The block's own sounds, now and then behind the rain while a door is open. r is the client's random source (cosmetic only:
// nothing here follows the visitor or the round). There is never a lift: the block has none, and a visitor who mentions one is lying.
// halloween: only while the Halloween edition runs.
export const AMBIENT = {
  drips(ctx, out, t, r = () => 0.5) { // the gutter dripping onto the sill, a few plinks at uneven gaps
    let at = 0;
    for (let i = 0, n = 4 + Math.floor(r() * 4); i < n; i++) {
      const f = 1400 + r() * 900;
      tone(ctx, out, t + at, { f0: f, f1: f * 0.55, d: 0.06, g: 0.5 });
      burst(ctx, out, t + at, { f: 4000, q: 1, d: 0.012, g: 0.25, type: 'highpass' });
      at += 0.35 + r() * 0.75;
    }
  },
  car(ctx, out, t, r = () => 0.5) { // a car going by on the wet street: tyre hiss swelling and fading, a low engine under it
    const d = 4 + r() * 1.5;
    swell(ctx, out, t, { d, peakAt: 0.45, f0: 500, fPeak: 1500, f1: 600, q: 0.9, g: 1.1 });
    swell(ctx, out, t, { d, peakAt: 0.45, f0: 90, fPeak: 140, f1: 80, q: 3, g: 1.4 });
  },
  wind(ctx, out, t, r = () => 0.5) { // a gust round the building, with a thin whistle in the window frame
    const d = 5 + r() * 2;
    swell(ctx, out, t, { d, peakAt: 0.5, f0: 250, fPeak: 900, f1: 300, q: 0.7, g: 1.2, type: 'lowpass' });
    swell(ctx, out, t + 0.6, { d: d - 1, peakAt: 0.5, f0: 700, fPeak: 1100, f1: 800, q: 9, g: 0.5 });
  },
  steps(ctx, out, t, r = () => 0.5) { // someone crossing the floor above, walking away
    for (let i = 0, n = 5 + Math.floor(r() * 4), at = 0; i < n; i++, at += 0.42 + r() * 0.14) {
      const k = 1 - (i / n) * 0.6;
      tone(ctx, out, t + at, { f0: 90, f1: 55, d: 0.12, g: 0.7 * k });
      burst(ctx, out, t + at, { f: 300, q: 0.7, d: 0.08, g: 1.2 * k, type: 'lowpass' });
    }
  },
  pipes(ctx, out, t, r = () => 0.5) { // the radiator knocking as the heating comes on: a few metal clanks
    for (let i = 0, n = 2 + Math.floor(r() * 3), at = 0; i < n; i++, at += 0.25 + r() * 0.45) {
      const f = 180 + r() * 80;
      [[1, 0.25, 0.3], [2.76, 0.18, 0.15], [5.4, 0.1, 0.08]].forEach(([m, d, g]) => tone(ctx, out, t + at, { f0: f * m, d, g }));
      burst(ctx, out, t + at, { f: 2000, q: 3, d: 0.02, g: 0.6 });
    }
  },
  door(ctx, out, t) { // a door shutting somewhere upstairs, and its latch
    burst(ctx, out, t, { f: 220, q: 0.7, d: 0.25, g: 3, type: 'lowpass' });
    tone(ctx, out, t, { f0: 70, f1: 45, d: 0.3, g: 0.6 });
    burst(ctx, out, t + 0.05, { f: 2500, q: 5, d: 0.02, g: 0.5 });
  },
  bell(ctx, out, t, r = () => 0.5) { // the church clock across the square, one to three strokes, far off
    const o = far(ctx, out, 1800);
    for (let i = 0, n = 1 + Math.floor(r() * 3); i < n; i++) {
      [[0.5, 0.25], [1, 0.35], [1.183, 0.2], [1.506, 0.12], [2, 0.1], [2.514, 0.06]].forEach(([m, g]) => tone(ctx, o, t + i * 2.2, { f0: 196 * m, d: 3.5, g }));
    }
  },
  dog(ctx, out, t, r = () => 0.5) { // a dog a few streets away, barking two to four times
    const o = far(ctx, out, 1200);
    for (let i = 0, n = 2 + Math.floor(r() * 3), at = 0; i < n; i++, at += 0.3 + r() * 0.3) {
      tone(ctx, o, t + at, { type: 'sawtooth', f0: 520, f1: 340, d: 0.13, g: 0.5 });
      burst(ctx, o, t + at, { f: 900, q: 1.5, d: 0.1, g: 0.8 });
    }
  },
  howl(ctx, out, t) { // Halloween: a long howl far off, rising and settling
    const o = far(ctx, out, 1500), osc = ctx.createOscillator(), lfo = ctx.createOscillator(), lg = ctx.createGain(), a = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(380, t); osc.frequency.linearRampToValueAtTime(620, t + 0.6); osc.frequency.linearRampToValueAtTime(520, t + 2.4);
    lfo.frequency.setValueAtTime(5, t); lg.gain.setValueAtTime(8, t); lfo.connect(lg); lg.connect(osc.frequency);
    a.gain.setValueAtTime(0.0001, t); a.gain.linearRampToValueAtTime(0.5, t + 0.5); a.gain.linearRampToValueAtTime(0.0001, t + 2.4);
    osc.connect(a); a.connect(o); osc.start(t); lfo.start(t); osc.stop(t + 2.5); lfo.stop(t + 2.5);
  },
};
// How often each comes (drips twice as often once the rain eases), and each one's level under the master gain
// (measured by studio/tools/audio-check.js: every one sits well under the knock and the stings).
export const AMBIENT_W = { drips: 3, car: 2, wind: 2, steps: 2, pipes: 2, door: 2, bell: 1, dog: 1, howl: 1 };
export const AMBIENT_GAIN = { drips: 0.12, car: 0.1, wind: 0.1, steps: 0.12, pipes: 0.13, door: 0.1, bell: 0.1, dog: 0.12, howl: 0.09 };
export const HALLOWEEN_ONLY = new Set(['howl']);
// the next sound of the block: never the last one again, Halloween's only in the edition, drips more when the rain eases
export function nextAmbient(r, { last = null, halloween = false, weather = 'steady' } = {}) {
  const ids = Object.keys(AMBIENT).filter(id => id !== last && (halloween || !HALLOWEEN_ONLY.has(id)));
  return pick(ids, r, id => AMBIENT_W[id] * (id === 'drips' && (weather === 'light' || weather === 'easing') ? 2 : 1));
}

// ---- the glitch (game/glitch.js, client/toy.js): toy town for a door or two ----
// The sting as the screen tears, in and out: a crushed noise burst and a stepped digital chirp falling 1200 to 200 Hz.
export function glitchSting(ctx, out, t) {
  burst(ctx, out, t, { f: 3000, q: 0.7, d: 0.08, g: 1.2 });
  for (let k = 0; k < 6; k++) tone(ctx, out, t + k * 0.045, { type: 'square', f0: 1200 - k * 180, d: 0.04, g: 0.35 });
  burst(ctx, out, t + 0.28, { f: 500, q: 2, d: 0.06, g: 1 });
}
export const GLITCH_GAIN = 0.35;
// While toy town shows, the rain goes muffled and mechanical: a low-pass at TOY_RAIN_CUT, crushed to TOY_CRUSH steps,
// and chopped by a square wave at TOY_CHOP_HZ (between 1 - TOY_CHOP_DEPTH and 1 of its level). Thunder and the block keep away.
export const TOY_RAIN_CUT = 900, TOY_CRUSH = 10, TOY_CHOP_HZ = 7, TOY_CHOP_DEPTH = 0.35;
const crushCurves = new WeakMap();
function crushCurve(ctx) {
  if (!crushCurves.has(ctx)) {
    const n = 1024, c = new Float32Array(n);
    for (let i = 0; i < n; i++) { const x = (i / (n - 1)) * 2 - 1; c[i] = Math.round(x * TOY_CRUSH) / TOY_CRUSH; }
    crushCurves.set(ctx, c);
  }
  return crushCurves.get(ctx);
}

const defaultStorage = () => { try { return globalThis.localStorage ?? null; } catch { return null; } };
const defaultLoad = async url => { const r = await fetch(url); if (!r.ok) throw new Error(url); return r.arrayBuffer(); };
const defaultTimers = { set: (fn, ms, _label) => { const h = setTimeout(fn, ms); h?.unref?.(); return h; }, clear: h => clearTimeout(h) };
const defaultContext = () => new (globalThis.AudioContext ?? globalThis.webkitAudioContext)();

export function createSound({ storage = defaultStorage(), makeContext = defaultContext, isRound = () => false, loadFile = defaultLoad, timers = defaultTimers, rng = Math.random, now = () => Date.now(), isHalloween = () => false } = {}) {
  let muted = false, unlocked = false, ctx = null, master = null;
  let wantNight = false, outbreakOn = false, busyUntil = 0, thunderTimer = null, thunderNow = null;
  let weather = pick(WEATHER, rng()), weatherTimer = null, ambientTimer = null, lastAmbient = null, toyOn = false;
  const beds = new Map(), buffers = new Map();
  const looping = new Map();
  try { muted = storage?.getItem(MUTE_KEY) === '1'; } catch { /* private mode */ }

  const stopLoops = () => {
    for (const stop of looping.values()) stop();
    looping.clear();
    for (const b of beds.values()) { b.src?.stop(); try { b.lfo?.stop(); } catch { /* stopped */ } }
    beds.clear(); timers.clear(thunderTimer); thunderTimer = null; thunderNow = null;
    timers.clear(weatherTimer); weatherTimer = null; timers.clear(ambientTimer); ambientTimer = null;
  };
  const api = {
    looping,
    get muted() { return muted; },
    get unlocked() { return unlocked; },
    setMuted(v) {
      muted = !!v;
      try { storage?.setItem(MUTE_KEY, muted ? '1' : '0'); } catch { /* private mode */ }
      if (muted) stopLoops(); else { humIfRound(); if (wantNight) night(); }
    },
    preview(v) { muted = !!v; if (muted) stopLoops(); }, // the demo flag: the button shows muted, nothing is saved
    toggle() { api.setMuted(!muted); return muted; },
    // Call from a click or key press; the browser only allows audio after one. A muted client still counts as unlocked
    // but builds nothing until it plays.
    unlock() {
      if (unlocked) return;
      unlocked = true;
      if (muted) return;
      ensure();
      humIfRound();
      if (isRound()) night();
    },
    night,
    outbreak(on) { outbreakOn = !!on; if (wantNight && !muted) syncBeds(); },
    special(name) { // a one-shot file beside the generated knock or sting
      if (!SPECIALS.has(name) || muted || !unlocked || !ensure()) return;
      markBusy(3000); duckThunder();
      load(name).then(buf => { if (buf && !muted) { shot(name, buf); markBusy(buf.duration * 1000); } });
    },
    play(event, halloween = false, size = 'plain') {
      if (muted || !unlocked || !EVENTS.includes(event)) return null;
      if (!ensure()) return null;
      const id = soundFor(event, halloween);
      if (looping.has(id)) return id;
      if (BUSY_MS[event]) { markBusy(BUSY_MS[event]); duckThunder(); }
      try {
        const stop = GENERATORS[id](ctx, master, ctx.currentTime + 0.01, Math.max(0, ['plain', 'gold', 'red'].indexOf(size)));
        if (typeof stop === 'function') looping.set(id, stop);
      } catch { return null; }
      return id;
    },
    // the glitch's sting, in and out
    glitch() {
      if (muted || !unlocked || !ensure()) return;
      const g = ctx.createGain(); g.gain.setValueAtTime(GLITCH_GAIN, ctx.currentTime); g.connect(master);
      try { glitchSting(ctx, g, ctx.currentTime + 0.01); } catch { /* skipped */ }
    },
    // toy town on or off: the rain goes muffled and mechanical, and back
    toy(on) { toyOn = !!on; const b = beds.get('rain-loop'); if (b?.gain && ctx) toyRain(b); },
    get toyOn() { return toyOn; },
    stop(event) { const stop = looping.get(event); if (stop) { stop(); looping.delete(event); } },
    // leaving a door: every loop and the rain stop; the next round's night() starts them again
    quiet() { wantNight = false; stopLoops(); },
  };
  // ---- night: rain behind the round, thunder over it, the outbreak beds crossfaded in; files are fetched on first use ----
  function load(name) {
    if (!buffers.has(name)) buffers.set(name, loadFile(FILE_URL[name]).then(ab => ctx.decodeAudioData(ab)).catch(() => { buffers.delete(name); return null; }));
    return buffers.get(name);
  }
  const markBusy = ms => { busyUntil = Math.max(busyUntil, now() + ms); };
  const level = name => (!wantNight ? 0 : name === 'rain-loop' ? FILE_GAIN[name] * weather.rain * (outbreakOn ? RAIN_DUCK : 1) : outbreakOn ? FILE_GAIN[name] : 0);
  function ramp(g, to, secs = FADE_S) {
    const t = ctx.currentTime;
    g.gain.cancelScheduledValues(t); g.gain.setValueAtTime(g.gain.value, t); g.gain.linearRampToValueAtTime(Math.max(to, SILENT), t + secs);
  }
  function shot(name, buf, { rate = 1, k = 1, cut = 0 } = {}) {
    const src = ctx.createBufferSource(), g = ctx.createGain();
    src.buffer = buf; src.loop = false; src.playbackRate?.setValueAtTime(rate, ctx.currentTime); g.gain.setValueAtTime(FILE_GAIN[name] * k, ctx.currentTime);
    src.connect(g); g.connect(cut ? far(ctx, master, cut) : master); src.start(ctx.currentTime + 0.01);
    return g;
  }
  function syncBed(name) {
    const want = level(name), b = beds.get(name);
    if (!b) {
      if (!want || !ensure()) return;
      const nb = { src: null, gain: null }; beds.set(name, nb);
      load(name).then(buf => {
        if (beds.get(name) !== nb) return;
        if (!buf) { beds.delete(name); return; }
        const src = ctx.createBufferSource(), g = ctx.createGain(), rain = name === 'rain-loop';
        src.buffer = buf; src.loop = true; g.gain.setValueAtTime(SILENT, ctx.currentTime);
        src.connect(g);
        if (rain) { // the rain never starts at the same spot or speed, and its weather sets how near it sounds
          src.playbackRate?.setValueAtTime(0.94 + rng() * 0.12, ctx.currentTime);
          nb.chop = ctx.createGain(); nb.chop.gain.setValueAtTime(1, ctx.currentTime); nb.chop.connect(master);
          let after = nb.chop;
          if (ctx.createWaveShaper) { nb.crush = ctx.createWaveShaper(); nb.crush.connect(nb.chop); after = nb.crush; }
          nb.filter = far(ctx, after, weather.cut); g.connect(nb.filter);
        } else g.connect(master);
        src.start(ctx.currentTime + 0.01, rain ? rng() * (buf.duration || 0) : 0);
        nb.src = src; nb.gain = g; fade(name, nb);
        if (rain && toyOn) toyRain(nb);
      });
    } else if (b.gain) fade(name, b);
  }
  function fade(name, b) {
    const want = level(name);
    ramp(b.gain, want);
    if (!want) { b.src.stop(ctx.currentTime + FADE_S + 0.1); beds.delete(name); }
  }
  const syncBeds = () => { for (const n of ['rain-loop', 'outbreak-loop', 'outbreak-sirens']) syncBed(n); };
  function night() { // from a round start, after the first gesture
    if (!unlocked) return;
    wantNight = true;
    if (muted || !ensure()) return;
    syncBeds();
    if (!thunderTimer) scheduleThunder();
    if (!weatherTimer) scheduleWeather();
    if (!ambientTimer) scheduleAmbient();
  }
  function toyRain(b) {
    const t = ctx.currentTime, f = b.filter?.frequency;
    if (f) { f.cancelScheduledValues(t); f.setValueAtTime(f.value || weather.cut, t); f.exponentialRampToValueAtTime(toyOn ? TOY_RAIN_CUT : weather.cut, t + 0.3); }
    if (b.crush) b.crush.curve = toyOn ? crushCurve(ctx) : null;
    if (b.lfo) { try { b.lfo.stop(); } catch { /* stopped */ } b.lfo = null; }
    if (toyOn && b.chop) {
      const lfo = ctx.createOscillator(), depth = ctx.createGain();
      lfo.type = 'square'; lfo.frequency.setValueAtTime(TOY_CHOP_HZ, t); depth.gain.setValueAtTime(TOY_CHOP_DEPTH / 2, t);
      b.chop.gain.setValueAtTime(1 - TOY_CHOP_DEPTH / 2, t); lfo.connect(depth); depth.connect(b.chop.gain); lfo.start(t); b.lfo = lfo;
    } else if (b.chop) b.chop.gain.setValueAtTime(1, t);
  }
  const scheduleWeather = () => { weatherTimer = timers.set(changeWeather, WEATHER_MIN_MS + rng() * WEATHER_SPREAD_MS, 'weather'); };
  function changeWeather() { // the rain moves to another weather over WEATHER_FADE_S
    weatherTimer = null;
    if (muted || !wantNight) return;
    weather = pick(WEATHER.filter(w => w !== weather), rng());
    scheduleWeather();
    const b = beds.get('rain-loop');
    if (!b?.gain) return;
    ramp(b.gain, level('rain-loop'), WEATHER_FADE_S);
    const f = b.filter?.frequency, t = ctx.currentTime;
    if (f) { f.cancelScheduledValues(t); f.setValueAtTime(f.value || weather.cut, t); f.exponentialRampToValueAtTime(weather.cut, t + WEATHER_FADE_S); }
  }
  const scheduleAmbient = ms => { timers.clear(ambientTimer); ambientTimer = timers.set(ambient, ms ?? AMBIENT_MIN_GAP_MS + rng() * AMBIENT_SPREAD_MS, 'block'); };
  function ambient() { // one of the block's sounds, never over a knock or a sting, panned somewhere around the door
    ambientTimer = null;
    if (muted || !wantNight) return;
    if (now() < busyUntil) return scheduleAmbient(2000);
    scheduleAmbient();
    if (toyOn) return; // toy town has no block round it
    const id = nextAmbient(rng(), { last: lastAmbient, halloween: isHalloween(), weather: weather.id });
    lastAmbient = id;
    try {
      const g = ctx.createGain(); g.gain.setValueAtTime(AMBIENT_GAIN[id], ctx.currentTime);
      if (ctx.createStereoPanner) { const p = ctx.createStereoPanner(); p.pan.setValueAtTime(rng() * 1.2 - 0.6, ctx.currentTime); g.connect(p); p.connect(master); } else g.connect(master);
      AMBIENT[id](ctx, g, ctx.currentTime + 0.01, rng);
    } catch { /* a sound that cannot be built is skipped */ }
  }
  const scheduleThunder = ms => { timers.clear(thunderTimer); thunderTimer = timers.set(thunder, ms ?? THUNDER_MIN_GAP_MS + rng() * THUNDER_SPREAD_MS, 'thunder'); };
  function thunder() {
    thunderTimer = null;
    if (muted || !wantNight) return;
    if (now() < busyUntil) return scheduleThunder(1500); // never over a knock or a sting
    scheduleThunder();
    if (toyOn || rng() >= weather.thunder) return; // a lighter rain brings less thunder, an easing one none; toy town none
    // each roll differs: slower is bigger and further, some come muffled from across the city
    const rate = 0.82 + rng() * 0.3, k = 0.55 + rng() * 0.45, cut = rng() < 0.4 ? 700 : 0;
    load('thunder').then(buf => {
      if (!buf || muted || now() < busyUntil) return;
      thunderNow = { gain: shot('thunder', buf, { rate, k, cut }), until: now() + (buf.duration / rate) * 1000 };
    });
  }
  function duckThunder() {
    if (thunderNow && now() < thunderNow.until) { ramp(thunderNow.gain, SILENT, 0.15); thunderNow = null; }
  }
  function humIfRound() { if (unlocked && isRound()) api.play('hum'); }
  function ensure() {
    if (ctx) return ctx;
    try {
      ctx = makeContext(); master = ctx.createGain(); master.gain.setValueAtTime(0.5, 0); master.connect(ctx.destination);
      if (ctx.state === 'suspended') ctx.resume();
    } catch { ctx = null; return null; }
    return ctx;
  }
  return api;
}
